import { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPreview() {
  const { theme } = useTheme();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/blog`).then(res => {
      const payload = res.data?.data || res.data;
      if (Array.isArray(payload)) setPosts(payload);
    }).catch((err) => {
      console.error(err);
      setPosts([
        { id: '1', title: 'Cara Membangun Kepercayaan Pelanggan Lewat UI/UX', excerpt: 'Desain bukan soal estetika saja...', category: 'branding', thumbnail: '', created_at: '2025-12-01' },
        { id: '2', title: 'Panduan Menghitung ROI Website untuk Bisnis Jasa', excerpt: 'Investasi website bukan pengeluaran...', category: 'roi', thumbnail: '', created_at: '2025-11-15' },
        { id: '3', title: "Mengapa Website Bisnis Anda Butuh 'Jiwa'", excerpt: 'Fitur bisa ditiru. Narasi tidak.', category: 'studi-kasus', thumbnail: '', created_at: '2025-11-01' },
      ]);
    });
  }, []);

  const categoryColors = {
    branding: theme === 'dark' ? '#00FF85' : '#0D9488',
    roi: theme === 'dark' ? '#3A82FF' : '#2563EB',
    'studi-kasus': theme === 'dark' ? '#FF0099' : '#D97706',
  };

  return (
    <section
      data-testid="blog-section"
      id="blog"
      ref={ref}
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 lg:py-40">
        <div className="flex items-end justify-between mb-12">
          <div>
            <motion.span
              className="font-mono text-xs tracking-[0.2em] uppercase block mb-4"
              style={{ color: 'var(--accent-primary)' }}
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
            >
              Blog
            </motion.span>
            <motion.h2
              className="font-heading text-3xl md:text-5xl font-semibold tracking-tight"
              style={{ color: 'var(--text-primary)' }}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
            >
              Insight & Perspektif
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <motion.article
              key={post.id}
              className="glass-card overflow-hidden group cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.15 }}
              data-testid={`blog-card-${i}`}
            >
              {/* Thumbnail */}
              <div
                className="w-full h-48 overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${categoryColors[post.category]}15, ${categoryColors[post.category]}05)`,
                }}
              >
                {post.thumbnail && (
                  <img
                    src={post.thumbnail}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                )}
              </div>

              <div className="p-6">
                <span
                  className="font-mono text-[10px] tracking-wider uppercase"
                  style={{ color: categoryColors[post.category] }}
                >
                  {post.category}
                </span>
                <h3 className="font-heading text-lg font-semibold mt-2 mb-3 leading-snug transition-colors" style={{ color: 'var(--text-primary)' }}>
                  {post.title}
                </h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
                  {post.excerpt}
                </p>
                <div className="flex items-center gap-1 text-sm font-medium transition-colors" style={{ color: 'var(--accent-primary)' }}>
                  Baca selengkapnya
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
